import { ValidatorFn } from '@angular/forms';
import { GrocyPurchaseProduct } from '../../../../core/entities/grocy/product-actions/product-action.entity';
import { GrocyDashboardSubProduct } from '../../../../core/entities/grocy/products/dashboard-sub-product.entity';
import { GrocyQuantityUnitBasics } from '../../../../core/entities/grocy/quantity-units/quantity-unit-basics.entity';
import { GrocyBasics } from '../../../../core/entities/grocy/shared/basics.entity';
import { requiredNotNull } from '../../../../forms/form-control-options.constants';
import { BaseForm } from '../../../../forms/forms/base.form';
import { GrocyDashboardShoppingItemFormControls } from '../form-controls/dashboard-shopping-item.form-controls';
import { GrocyDashboardShoppingItemFormOptions } from '../form-options/dashboard-shopping-item.form-options';

export class GrocyDashboardShoppingItemForm extends BaseForm<GrocyDashboardShoppingItemFormControls> {
  formGroup = this.createFormGroup(GrocyDashboardShoppingItemFormControls);
  override options = new GrocyDashboardShoppingItemFormOptions();
  _product: GrocyDashboardSubProduct | null = null;

  get actualPrice(): number {
    return this.fullPrice - this.totalDiscount;
  }

  get amount(): number {
    return this.controls.amount.value || 1;
  }

  get conversionFactor(): number {
    return this.product?.quConversionFactorPurchaseToStock ?? 1;
  }

  get enteredPurchaseQuPrice(): number {
    return this.controls.purchaseQuPrice.value || 0;
  }

  get fullPrice(): number {
    return this.amount * this.purchaseQuPrice;
  }

  get newStock(): string {
    const currentStock = this.product?.stockAmount ?? 0;
    const newAmount = currentStock + this.amount * this.conversionFactor;

    const quantityUnit =
      newAmount === 1 ? this.product?.quantityUnits.stock?.name : this.product?.quantityUnits.stock?.namePlural;

    return `${currentStock} → ${newAmount} ${quantityUnit}`;
  }

  get pantIncluded(): boolean {
    return this.controls.pantIncluded.value || false;
  }

  get product(): GrocyDashboardSubProduct | null {
    return this._product;
  }

  get purchaseQuPant(): number {
    return this.controls.purchaseQuPant.value || 0;
  }

  get purchaseQuPrice(): number {
    if (this.pantIncluded) {
      return this.enteredPurchaseQuPrice - this.purchaseQuPant;
    } else return this.enteredPurchaseQuPrice;
  }

  get quantityUnit(): GrocyQuantityUnitBasics | undefined {
    return this.product?.quantityUnits.purchase;
  }

  get quantityUnitName(): string {
    if (this.quantityUnit) {
      return this.amount === 1 ? this.quantityUnit.name : this.quantityUnit.namePlural;
    } else return '';
  }

  get totalDiscount(): number {
    return this.controls.totalDiscount.value || 0;
  }

  get totalPant(): number {
    return this.amount * this.purchaseQuPant;
  }

  set product(product: GrocyDashboardSubProduct | null) {
    this._product = product;

    if (product) {
      this.setInitialAmount(product);
      this.setInitialBestBeforeDate(product);
      this.setInitialLocation(product);
      this.setInitialPrice(product);
    }

    this.setChangeListener();
  }

  disable(): void {
    this.controls.amount.disable();
    this.controls.bestBeforeDate.disable();
    this.controls.neverOverdue.disable();
    this.controls.pantIncluded.disable();
    this.controls.purchaseQuPant.disable();
    this.controls.purchaseQuPrice.disable();
    this.controls.totalDiscount.disable();
  }

  enable(): void {
    this.controls.amount.enable();
    this.controls.neverOverdue.enable();
    this.controls.pantIncluded.enable();
    this.controls.purchaseQuPant.enable();
    this.controls.purchaseQuPrice.enable();
    this.controls.totalDiscount.enable();

    if (this.controls.neverOverdue.value === false) {
      this.controls.bestBeforeDate.enable();
    }
  }

  getProductAction(purchased: Date, store: GrocyBasics): GrocyPurchaseProduct {
    const amount = this.amount * this.conversionFactor;
    const price = this.actualPrice / amount;

    return {
      amount,
      bestBefore: this.controls.neverOverdue.value
        ? '2999-12-31'
        : this.controls.bestBeforeDate.value!.toISOString().split('T')[0],
      locationId: this.controls.location.value!.id,
      note: '',
      price,
      productId: this.product!.id,
      purchased: purchased.toISOString().split('T')[0],
      shoppingLocationId: store.id,
    };
  }

  private setChangeListener(): void {
    this.controls.neverOverdue.valueChanges.subscribe(value => {
      if (this.controls.neverOverdue.disabled) {
        return;
      }

      if (value === false) {
        this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
        this.controls.bestBeforeDate.setValue(new Date());
        this.controls.bestBeforeDate.enable();
      } else {
        this.controls.bestBeforeDate.clearValidators();
        this.controls.bestBeforeDate.setValue(null);
        this.controls.bestBeforeDate.disable();
      }
    });
  }

  private setInitialAmount({ defaultAmount }: GrocyDashboardSubProduct): void {
    this.controls.amount.setValue(defaultAmount);
  }

  private setInitialBestBeforeDate({ nextDueAt }: GrocyDashboardSubProduct): void {
    if (nextDueAt) {
      const nextDueDate = new Date(nextDueAt);

      if (nextDueDate < new Date('2999-12-31')) {
        this.controls.bestBeforeDate.setValue(nextDueDate);
        this.controls.neverOverdue.setValue(false);
        this.controls.bestBeforeDate.enable();
      } else {
        this.controls.neverOverdue.setValue(true);
        this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
        this.controls.bestBeforeDate.setValue(null);
        this.controls.bestBeforeDate.disable();
      }
    } else {
      this.controls.neverOverdue.setValue(true);
      this.controls.bestBeforeDate.clearValidators();
      this.controls.bestBeforeDate.setValue(null);
      this.controls.bestBeforeDate.disable();
    }
  }

  private setInitialLocation({ location }: GrocyDashboardSubProduct): void {
    if (location) {
      this.controls.location.setValue(this.options.grocyLocations.find(l => l.id === location.id)!);
    }
  }

  private setInitialPrice({ prices }: GrocyDashboardSubProduct): void {
    if (prices) {
      this.controls.purchaseQuPrice.setValue(prices.current * this.conversionFactor);
    }
  }
}
